import { useEffect, useRef } from 'react';

import { TaskView } from '@ndvr/customer-api';

import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';

import { useTaskViewState } from '../task-state';
import { getTaskAssigneeInitials, getTaskAssigneeLabel } from '../task-utils';
import { TaskAssigneeAvatar } from './TaskAssigneeAvatar';

interface TaskAssigneeFilterContentProps {
    tasks: TaskView[];
    searchTerm: string;
    onSearchChange: (value: string) => void;
    focusSearchTrigger: number;
}

export const TaskAssigneeFilter = ({
    tasks,
    searchTerm,
    onSearchChange,
    focusSearchTrigger,
}: TaskAssigneeFilterContentProps) => {
    const { taskViewState, setTaskViewState } = useTaskViewState();
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Get unique assignees from the provided task data
    const availableAssignees = (() => {
        const assigneeByGuid = new Map<string, { guid: string; name: string; initials: string }>();
        tasks.forEach((task) => {
            if (task.assignee && !assigneeByGuid.has(task.assignee)) {
                assigneeByGuid.set(task.assignee, {
                    guid: task.assignee,
                    name: getTaskAssigneeLabel(task),
                    initials: getTaskAssigneeInitials(task),
                });
            }
        });
        return [...assigneeByGuid.values()].sort((a, b) => a.name.localeCompare(b.name));
    })();

    // Filter assignees based on search term
    const filteredAssignees = availableAssignees.filter((assignee) =>
        assignee.name.toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const handleAssigneeToggle = (assigneeGuid: string) => {
        const currentSelected = taskViewState.selectedAssignees;
        const isSelected = currentSelected.includes(assigneeGuid);

        const newSelected = isSelected
            ? currentSelected.filter((guid) => guid !== assigneeGuid)
            : [...currentSelected, assigneeGuid];

        setTaskViewState({ selectedAssignees: newSelected });
    };

    // Focus search input when requested
    useEffect(() => {
        if (focusSearchTrigger > 0) {
            searchInputRef.current?.focus();
        }
    }, [focusSearchTrigger]);

    return (
        <div className="flex h-full flex-col space-y-3">
            <div className="p-4 pt-2">
                <Input
                    ref={searchInputRef}
                    placeholder="Search assignees..."
                    value={searchTerm}
                    onChange={(e) => onSearchChange(e.target.value)}
                    className="w-full"
                />
            </div>
            <div className="flex-1 overflow-y-auto">
                <div className="space-y-2">
                    {filteredAssignees.length === 0 ? (
                        <div className="py-4 text-center text-sm text-gray-500">No assignees found</div>
                    ) : (
                        filteredAssignees.map((assignee) => {
                            const isSelected = taskViewState.selectedAssignees.includes(assignee.guid);
                            return (
                                <div
                                    key={assignee.guid}
                                    className="flex cursor-pointer items-center space-x-3 rounded p-2 hover:bg-gray-50"
                                    onClick={() => handleAssigneeToggle(assignee.guid)}
                                >
                                    <Checkbox checked={isSelected} />
                                    <TaskAssigneeAvatar
                                        guid={assignee.guid}
                                        initials={assignee.initials}
                                        className="h-8 w-8 text-xs"
                                    />
                                    <span className="text-sm">{assignee.name}</span>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};
